import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowRight, Star, MapPin, Clock, Users } from 'lucide-react';
import heroimg from '../assets/heroimg.jpg';
import facial from '../assets/facial.jpg';
import haircolor from '../assets/haircolor.jpg';
import haircut from '../assets/haircut.jpg';
import beardtrim from '../assets/beardtrim.jpg';
import serviceAPI from '../services/serviceAPI';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [services, setServices] = useState([]);

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await serviceAPI.getAllServices({ isActive: true });
        // Get first 4 services for homepage display
        setServices(response.data.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch services:', error);
        // Fallback to default services if API fails
        setServices([
          {
            _id: '1',
            name: 'Haircut',
            price: 500,
            description: 'Professional haircut with precision styling',
            image: haircut,
          },
          {
            _id: '2',
            name: 'Beard Trim',
            price: 300,
            description: 'Expert beard grooming and shaping',
            image: beardtrim,
          },
          {
            _id: '3',
            name: 'Facial',
            price: 400,
            description: 'Rejuvenating facial treatment',
            image: facial,
          },
          {
            _id: '4',
            name: 'Hair Color',
            price: 800,
            description: 'Professional hair coloring service',
            image: haircolor,
          },
        ]);
      }
    };
    
    fetchServices();
  }, []);

  // Helper function to get service image with fallback
  const getServiceImage = (service) => {
    // First check if service has a valid Cloudinary URL
    if (service.image && (service.image.startsWith('http://') || service.image.startsWith('https://'))) {
      return service.image;
    }
    
    // Fallback to local images based on service name or category
    const name = (service.name || '').toLowerCase();
    const category = (service.category || '').toLowerCase();
    
    if (name.includes('haircut') || category.includes('haircut')) return haircut;
    if (name.includes('beard') || category.includes('beard')) return beardtrim;
    if (name.includes('facial') || category.includes('facial')) return facial;
    if (name.includes('color') || name.includes('colour') || category.includes('color')) return haircolor;
    if (name.includes('massage') || category.includes('massage')) return heroimg;
    
    // Final fallback
    return heroimg;
  };

  const testimonials = [
    {
      name: 'Raj Kumar',
      rating: 5,
      comment: 'Best salon in the city! Professional staff and great service.',
    },
    {
      name: 'Amit Singh',
      rating: 5,
      comment: 'Amazing experience. Will definitely come back.',
    },
    {
      name: 'Vikram Patel',
      rating: 4,
      comment: 'Very satisfied with the haircut. Friendly barbers.',
    },
  ];

  // Auto-carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000); // Change testimonial every 6 seconds
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Get visible testimonials (current, next, and next-next in rotation)
  const getVisibleTestimonials = () => {
    return [
      testimonials[currentTestimonialIndex],
      testimonials[(currentTestimonialIndex + 1) % testimonials.length],
      testimonials[(currentTestimonialIndex + 2) % testimonials.length],
    ];
  };

  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="hero min-h-screen text-white flex items-center" style={{backgroundImage: `url(${heroimg})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Welcome to Elite Salon
              </h1>
              <p className="text-lg sm:text-xl text-blue-100 mb-8">
                Premium grooming and barber services for the modern man. Book your appointment now
                and experience excellence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate(isAuthenticated ? '/booking' : '/signup')}
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition flex items-center justify-center space-x-2"
                >
                  <span>Book Your Appointment</span>
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => navigate('/services')}
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition"
                >
                  Explore Services
                </button>
              </div>
            </div>
            {/* <div className="text-6xl text-center">💈</div> */}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 relative" style={{backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${facial})`, backgroundAttachment: 'fixed', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-white drop-shadow-lg">Our Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => (
              <div
                key={service._id}
                className="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-700 ease-out transform hover:-translate-y-3 hover:scale-105 cursor-pointer"
              >
                {/* Service Image Background */}
                <div className="absolute inset-0 h-56 overflow-hidden bg-gray-300">
                  <img 
                    src={getServiceImage(service)} 
                    alt={service.name} 
                    className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-1000 ease-out" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white dark:to-gray-800"></div>
                </div>
                
                {/* Content */}
                <div className="relative pt-60 px-6 pb-6">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">{service.name}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{service.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      ₹{service.discountPrice || service.price}
                    </span>
                    <button
                      onClick={() => navigate(isAuthenticated ? '/booking' : '/signup')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-500 ease-out transform hover:scale-110"
                    >
                      Book
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 dark:text-white">Why Choose Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-md">
              <div className="text-4xl mb-4">👨‍💼</div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">Expert Professionals</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Highly trained and experienced barbers with expertise in all grooming services.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-md">
              <div className="text-4xl mb-4">⏰</div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">Online Booking</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Easy online appointment booking. Choose your preferred time and barber.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-md">
              <div className="text-4xl mb-4">⭐</div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">Quality Service</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Premium quality products and service with customer satisfaction guarantee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 dark:text-white">Customer Reviews</h2>
          
          {/* Auto Carousel - 3 Testimonials Moving Left to Right */}
          <div className="relative w-full overflow-hidden">
            <div className="flex gap-6 transition-transform duration-1000 ease-in-out">
              {getVisibleTestimonials().map((testimonial, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-full md:w-1/3 bg-white dark:bg-gray-700 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
                  style={{
                    animation: `slideIn 1000ms ease-in-out forwards`,
                    animationDelay: `${index * 200}ms`,
                  }}
                >
                  <div className="flex items-center space-x-2 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-200 text-lg mb-6 italic">
                    "{testimonial.comment}"
                  </p>
                  <p className="font-bold text-gray-900 dark:text-white text-lg">{testimonial.name}</p>
                </div>
              ))}
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonialIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonialIndex
                      ? 'bg-blue-600 w-8'
                      : 'bg-gray-400 dark:bg-gray-500 hover:bg-gray-500'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* CSS Animation for slide-in effect */}
          <style>{`
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateX(20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
          `}</style>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Look Your Best?</h2>
          <p className="text-lg mb-8 text-blue-100">
            Book your appointment now and experience professional grooming like never before.
          </p>
          <button
            onClick={() => navigate(isAuthenticated ? '/booking' : '/signup')}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition"
          >
            Book Appointment Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
